import { useEffect, useRef, useState, useMemo } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import { useAppTranslation } from "locale/useAppTranslation";
import { vehicleTypePageRouteName } from "../list/index.page";
import {
  useVehicleTypeServiceV1ListVehicleTypeCategoriesQuery,
  useVehicleTypeServiceV1ListTempTypesQuery,
  useVehicleTypeServiceV1BatchCreateVehicleTypeMutation,
} from "_services/modules/vehicle-type";
import { toast } from "react-toastify";
import { errorHandler } from "_services/errorHandler";

interface VehicleTypeItem {
  categoryId: string;
  vehicleType: string;
  tempTypeId: string;
  isChassis: boolean;
  isContainer: boolean;
  status: string;
}

export interface AddVehicleTypeFormValues {
  categoryId: string;
  vehicleType: string;
  tempTypeId: string;
  isChassis: boolean;
  isContainer: boolean;
  status: string;
  items: VehicleTypeItem[];
}

const schema = yup.object({
  categoryId: yup.string().required("Vehicle category is required"),
  vehicleType: yup.string().required("Vehicle type is required"),
  tempTypeId: yup.string(),
  isChassis: yup.boolean().default(false),
  isContainer: yup.boolean().default(false),
  status: yup.string().required(),
  items: yup.array().of(
    yup.object({
      categoryId: yup.string().required("Vehicle category is required"),
      vehicleType: yup.string().required("Vehicle type is required"),
      tempTypeId: yup.string().default(""),
      isChassis: yup.boolean().default(false),
      isContainer: yup.boolean().default(false),
      status: yup.string().required("Status is required"),
    })
  ).required(),
}).required();

const useAddVehicleTypeForm = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useAppTranslation("vehicleType");

  const { data: categoriesData } = useVehicleTypeServiceV1ListVehicleTypeCategoriesQuery();
  const { data: tempTypesData } = useVehicleTypeServiceV1ListTempTypesQuery();
  const [batchCreateVehicleType] = useVehicleTypeServiceV1BatchCreateVehicleTypeMutation();

  const categoryOptions = useMemo(() => {
    return categoriesData?.vehicle_type_categories?.map((cat) => ({
      label: cat.category_name ?? "",
      value: cat.id ?? "",
      hasTempType: cat.has_temp_type ?? false,
      hasChassis: cat.has_chassis ?? false,
      hasContainer: cat.has_container ?? false,
    })) || [];
  }, [categoriesData]);

  const tempTypeOptions = useMemo(() => {
    return tempTypesData?.temp_types?.map((temp) => ({
      label: temp.name ?? "",
      value: temp.id ?? "",
    })) || [];
  }, [tempTypesData]);

  const {
    control,
    register,
    trigger,
    getValues,
    setValue,
    setError,
    watch,
    formState: { errors },
  } = useForm<AddVehicleTypeFormValues>({
    mode: "onBlur",
    resolver: yupResolver(schema) as any,
    defaultValues: {
      categoryId: "",
      vehicleType: "",
      tempTypeId: "",
      isChassis: false,
      isContainer: false,
      status: "active",
      items: [],
    },
  });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "items",
  });

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [showSaveModal, setShowSaveModal] = useState(false);

  const watchedCategoryId = watch("categoryId");
  const prevCategoryId = useRef(watchedCategoryId);

  const config = useMemo(() => {
    const category = categoryOptions.find((c) => c.value === watchedCategoryId);
    return {
      label: category?.label ?? "",
      hasTempType: category?.hasTempType ?? false,
      hasChassis: category?.hasChassis ?? false,
      hasContainer: category?.hasContainer ?? false,
    };
  }, [watchedCategoryId, categoryOptions]);

  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: t("page-title-add"),
        hasBackButton: true,
      })
    );
  }, [dispatch, t]);

  // Clear conditional fields when category changes
  useEffect(() => {
    if (prevCategoryId.current !== watchedCategoryId) {
      setValue("tempTypeId", "");
      setValue("isChassis", false);
      setValue("isContainer", false);
      prevCategoryId.current = watchedCategoryId;
    }
  }, [watchedCategoryId, setValue]);

  const handleAddToList = async () => {
    const fieldsToValidate: (keyof AddVehicleTypeFormValues)[] = [
      "categoryId",
      "vehicleType",
      "status",
    ];
    if (config.hasTempType) fieldsToValidate.push("tempTypeId");

    const isValid = await trigger(fieldsToValidate);
    if (!isValid) return;

    const values = getValues();

    // Duplicate check: vehicleType name must be unique
    const isDuplicate = fields.some(
      (item, index) =>
        item.vehicleType.toLowerCase() === values.vehicleType.toLowerCase() &&
        index !== editingIndex
    );

    if (isDuplicate) {
      setError("vehicleType", {
        type: "validate",
        message: t("error-duplicate-value"),
      });
      return;
    }

    const newItem: VehicleTypeItem = {
      categoryId: values.categoryId,
      vehicleType: values.vehicleType,
      tempTypeId: config.hasTempType ? values.tempTypeId : "",
      isChassis: config.hasChassis ? values.isChassis : false,
      isContainer: config.hasContainer ? values.isContainer : false,
      status: values.status,
    };

    if (editingIndex !== null) {
      update(editingIndex, newItem);
      setEditingIndex(null);
    } else {
      append(newItem);
    }

    resetFormFields();
  };

  const handleEditItem = (index: number) => {
    const item = fields[index];
    setEditingIndex(index);
    setValue("categoryId", item.categoryId);
    setValue("vehicleType", item.vehicleType);
    setValue("tempTypeId", item.tempTypeId);
    setValue("isChassis", item.isChassis);
    setValue("isContainer", item.isContainer);
    setValue("status", item.status);
  };

  const handleDeleteItem = (index: number) => {
    remove(index);
    if (editingIndex === index) {
      setEditingIndex(null);
      resetFormFields();
    } else if (editingIndex !== null && index < editingIndex) {
      setEditingIndex(editingIndex - 1);
    }
  };

  const handleDiscardChanges = () => {
    setEditingIndex(null);
    resetFormFields();
  };

  const resetFormFields = () => {
    setValue("vehicleType", "");
    setValue("tempTypeId", "");
    setValue("isChassis", false);
    setValue("isContainer", false);
    setValue("status", "active");
  };

  const handleSave = async () => {
    const payload = fields.map((f) => ({
      category_id: f.categoryId,
      vehicle_type: f.vehicleType,
      temp_type_id: f.tempTypeId || undefined,
      is_chassis: f.isChassis,
      is_container: f.isContainer,
      status: f.status,
    }));
    try {
      await batchCreateVehicleType({
        vehicleTypeV1BatchCreateVehicleTypeReq: { items: payload },
      }).unwrap();
      toast.success(t("toast-create-success"));
      setShowSaveModal(false);
      navigate(-1);
    } catch (error) {
      errorHandler(error);
      setShowSaveModal(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return {
    control,
    register,
    errors,
    fields,
    editingIndex,
    showSaveModal,
    setShowSaveModal,
    handleAddToList,
    handleEditItem,
    handleDeleteItem,
    handleDiscardChanges,
    handleSave,
    handleCancel,
    t,
    watchedCategoryId,
    config,
    categoryOptions,
    tempTypeOptions,
  };
};

export default useAddVehicleTypeForm;
